import { useCallback, useMemo } from 'react'
import { useMutation, useSuspenseQuery } from '@apollo/client'
import { AdminAPI, getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'

export function useInvites() {
  const { data } = useSuspenseQuery(AdminAPI.membershipInvite.GET)
  const [resendInvite] = useMutation(AdminAPI.membershipInvite.RESEND)
  const [updateInvite] = useMutation(AdminAPI.membershipInvite.UPDATE)
  const [deleteInvite] = useMutation(AdminAPI.membershipInvite.DELETE)

  const invites = useMemo(
    () =>
      data.membershipInvitesForCurrentOrg.map(invite => {
        return getFragment(
          SharedAPI.membershipInvite.MEMBERSHIP_INVITE_FRAGMENT,
          invite
        )
      }),
    [data]
  )

  const onUpdate = useCallback(
    (id: number, newRole: GraphQL.Role) => {
      const currentInvite = invites.find(invite => invite.id === id)

      if (!currentInvite) return

      updateInvite({
        variables: {
          input: {
            id,
            role: newRole
          }
        },
        optimisticResponse: {
          updateMembershipInvite: {
            ...currentInvite,
            role: newRole,
            expiresAt: currentInvite.expiresAt
          }
        }
      })
    },
    [invites, updateInvite]
  )

  const onResend = useCallback(
    (id: number) => {
      const currentInvite = invites.find(invite => invite.id === id)

      if (!currentInvite) return

      const nowDate = new Date().toISOString()

      resendInvite({
        variables: {
          id
        },
        optimisticResponse: {
          resendMembershipInvite: {
            ...currentInvite,
            role: currentInvite.role,
            expiresAt: nowDate
          }
        }
      })
    },
    [invites, resendInvite]
  )

  const onDelete = useCallback(
    (id: number) => {
      const currentInvite = invites.find(invite => invite.id === id)

      if (!currentInvite) return

      deleteInvite({
        variables: {
          id
        },
        optimisticResponse: {
          __typename: 'RootMutationType',
          deleteMembershipInvite: currentInvite
        },
        update(cache, { data }) {
          if (!data) return

          const normalizedId = cache.identify(data.deleteMembershipInvite)

          cache.modify<GraphQL.MembershipInvitesForCurrentOrgQuery>({
            id: 'ROOT_QUERY',
            fields: {
              membershipInvitesForCurrentOrg(
                existingInvites = [],
                { toReference }
              ) {
                return existingInvites.filter(
                  inviteRef => toReference(inviteRef)?.__ref !== normalizedId
                )
              }
            }
          })

          cache.evict({ id: normalizedId })
          cache.gc()
        }
      })
    },
    [invites, deleteInvite]
  )

  return {
    invites,
    onUpdate,
    onResend,
    onDelete
  }
}
